<template>
    <div class="ds-address-autocomplete">
        <DsCombobox
            v-model:open="isOpen"
            v-model:searchTerm="searchTerm"
            :highlightOnHover="false"
            ignoreFilter
            @input="handleSearchTermChange"
        >
            <DsComboboxAnchor class="w-full">
                <DsComboboxInput asChild>
                    <DsTextInput
                        v-model="searchTerm"
                        v-model:isFocused="isFocused"
                        :id
                        :disabled
                        :error
                        :label
                        :name
                        :placeholder
                        :required
                        type="text"
                    >
                        <template #leading>
                            <DsIcon
                                icon="fa-solid fa-magnifying-glass"
                                size="md"
                            />
                        </template>
                        <template #trailing>
                            <button
                                v-if="searchTerm && !disabled"
                                @click.stop="handleClear"
                            >
                                <DsIcon
                                    icon="fa-solid fa-circle-xmark"
                                    size="md"
                                />
                            </button>
                        </template>
                    </DsTextInput>
                </DsComboboxInput>
            </DsComboboxAnchor>

            <DsComboboxContent
                v-if="showContent"
                class="max-h-52 overflow-y-auto p-2"
                :sideOffset="props.error ? -20 : 4"
            >
                <div
                    v-if="showSpinner"
                    class="flex h-80 flex-col items-center justify-center p-4"
                >
                    <DsSpinner
                        size="md"
                        variant="primary"
                    />
                </div>

                <template v-if="showSuggestions">
                    <DsComboboxItem
                        v-for="suggestion in suggestions"
                        :key="suggestion.searchResult.place_id"
                        asChild
                        class="hover:bg-semantic-secondary-hover active:bg-semantic-secondary-pressed"
                        :value="suggestion.searchResult.place_id"
                        @select="handleItemSelected(suggestion)"
                    >
                        <div class="flex w-full cursor-pointer items-center gap-3 rounded-lg p-3">
                            <DsIcon
                                icon="fa-regular fa-location-dot"
                                size="md"
                            />
                            <div class="flex flex-1 flex-col items-start justify-start">
                                <p class="text-left text-content-lg font-medium text-content-primary">
                                    {{ suggestion.formattedComponents.streetNumber }}
                                    {{ suggestion.formattedComponents.streetName }}
                                </p>
                                <p class="text-content-medium text-left text-content-secondary">
                                    {{ suggestion.formattedComponents.city }}, {{ suggestion.formattedComponents.state }}
                                    {{ suggestion.formattedComponents.zipCode }}
                                </p>
                            </div>
                        </div>
                    </DsComboboxItem>
                </template>

                <DsComboboxEmpty v-if="showEmptyState">
                    <div class="flex flex-col items-center justify-center p-4">
                        <DsIcon
                            class="mb-4 text-content-primary"
                            icon="fa-solid fa-magnifying-glass"
                            size="lg"
                        />
                        <p class="mb-1 text-content-lg font-medium text-content-primary">No results found</p>
                        <p class="mb-3 text-content-md text-content-secondary">Adjust your search and try again</p>
                        <DsButton
                            size="sm"
                            variant="tertiary"
                            @click.stop="handleClear"
                        >
                            Clear search
                        </DsButton>
                    </div>
                </DsComboboxEmpty>
            </DsComboboxContent>
        </DsCombobox>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsCombobox from '@/components/design-system/form/combobox/DsCombobox.vue';
import DsComboboxAnchor from '@/components/design-system/form/combobox/DsComboboxAnchor.vue';
import DsComboboxContent from '@/components/design-system/form/combobox/DsComboboxContent.vue';
import DsComboboxEmpty from '@/components/design-system/form/combobox/DsComboboxEmpty.vue';
import DsComboboxInput from '@/components/design-system/form/combobox/DsComboboxInput.vue';
import DsComboboxItem from '@/components/design-system/form/combobox/DsComboboxItem.vue';
import type {
    AddressSuggestion,
    GooglePlaceDetailsResponse,
} from '@/components/design-system/form/input/DsAddressAutocomplete/address-autocomplete.types';
import {
    usePlaceDetails,
    useSearchPlaces,
} from '@/components/design-system/form/input/DsAddressAutocomplete/google-places.service';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import type { DsTextInputProps } from '@/components/design-system/form/input/text/text-input.types';
import DsSpinner from '@/components/design-system/spinner/DsSpinner.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import { debounce } from 'lodash';
import { computed, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        id: string;
        disabled?: DsTextInputProps['disabled'];
        label?: string;
        name?: DsTextInputProps['name'];
        placeholder?: string;
        error?: string;
        helperText?: string;
        required?: boolean;
    }>(),
    {
        label: 'Address',
        placeholder: 'Search address',
        required: false,
    },
);

const isOpen = ref(false);
const isFocused = ref(false);

const emits = defineEmits<{
    (event: 'locationSelected', payload: GooglePlaceDetailsResponse): void;
    (event: 'locationCleared'): void;
}>();

const searchTerm = defineModel<string>({ default: '' });
const suggestions = ref<AddressSuggestion[]>([]);
const hasSearched = ref(false);
const hasFetched = ref(false);

const { data: searchData, searchPlaces, isFetching } = useSearchPlaces();
const { data: placeDetails, getPlaceDetails } = usePlaceDetails();

watch(searchData, newData => {
    hasFetched.value = true;
    suggestions.value = newData ?? [];

    isOpen.value = !!searchTerm.value.trim();
});

const handleClear = () => {
    searchTerm.value = '';
    suggestions.value = [];
    isOpen.value = false;
    hasFetched.value = false;
    isFocused.value = true;
    emits('locationCleared');
};

const performSearch = () => {
    const query = searchTerm.value.trim();
    if (query) {
        hasSearched.value = true;
        hasFetched.value = false;
        isOpen.value = true;
        searchPlaces(query);
    } else {
        suggestions.value = [];
        hasSearched.value = false;
        hasFetched.value = false;
        isOpen.value = false;
    }
};

const handleSearchTermChange = debounce(performSearch, 500);

const handleItemSelected = (selectedSuggestion: AddressSuggestion) => {
    isOpen.value = false;

    if (selectedSuggestion?.searchResult?.place_id) {
        getPlaceDetails(selectedSuggestion.searchResult.place_id);
    }
};

watch(placeDetails, details => {
    if (details) {
        const matchingSuggestion = suggestions.value.find(s => s.searchResult.place_id === details.id);
        if (matchingSuggestion) {
            searchTerm.value = matchingSuggestion.label;
        }
        emits('locationSelected', details);
    }
});

const showSpinner = computed(() => {
    return isFetching.value;
});

const showEmptyState = computed(() => {
    return !isFetching.value && hasFetched.value && suggestions.value.length === 0;
});

const showSuggestions = computed(() => {
    return !isFetching.value && suggestions.value.length > 0;
});

const showContent = computed(() => {
    return showSuggestions.value || showSpinner.value || showEmptyState.value;
});
</script>
<style scoped lang="scss">
:deep([data-highlighted]:not(:hover)) {
    outline: 2px solid theme('colors.ds.primary.200') !important;
}
</style>
